import withLifecycle from '@hocs/with-lifecycle';
import { filter, head, keys, map, mapObjIndexed, omit, pick, pipe, pluck, reject, sum, toPairs, uniq } from 'ramda';
import { noop } from 'ramda-adjunct';
import { MouseEvent as ReactMouseEvent } from 'react';
import { compose, defaultProps, mapProps, withContext, withHandlers, withProps, withStateHandlers } from 'recompose';
import { LassoItemMargin } from '../../../constants/Lasso';
import { MediaManagerSDKAnyItem } from '../../../utils/media-manager-sdk';
import { ifRectanglesIntersect } from '../../../utils/rectangles/rectangles';
import { IRectangle } from '../../../utils/rectangles/rectangles.types';
import {
  AutoScrollDirection,
  IHandlers,
  IInner,
  ILassoItems,
  ILassoRectangleItems,
  IOuter,
  IState,
  TLassoRectangle,
  TLassoRectangleVertex,
  TRectanglePair,
  TStateUpdaters,
  contextTypes,
} from './Lasso.types';
import { getRectLowerBound } from './Lasso.utils';

const AUTO_SCROLL_DELTA = 30;
const AUTO_SCROLL_TIMEOUT_DURATION = AUTO_SCROLL_DELTA * 2;
const SCROLLBAR_WIDTH = 20;

export const enhance = compose<IInner, IOuter>(
  defaultProps({ itemMarginRight: LassoItemMargin.DEFAULT }),
  withStateHandlers<IState, TStateUpdaters, IOuter>(
    {
      lassoItems: {},
      selectedArea: null,
      hasFirstMoveDone: false,
    },
    {
      onLassoItemReady:
        ({ lassoItems }) =>
        (itemId, element) => ({
          lassoItems: {
            ...lassoItems,
            [itemId]: element,
          },
        }),
      onLassoItemDestroy:
        ({ lassoItems }) =>
        (itemId) => ({
          lassoItems: omit([itemId], lassoItems),
        }),
      setSelectedArea: () => (selectedArea) => ({ selectedArea }),
      setFirstMoveDone: () => (hasFirstMoveDone) => ({ hasFirstMoveDone }),
    },
  ),
  withHandlers<IOuter & IState & TStateUpdaters, IHandlers>(({ onSelectItems }) => {
    const itemsRectanglesKeeper: { rectangles: ILassoRectangleItems } = {
      rectangles: {},
    };

    let wrapperContentHeight: number = 0;
    let element: HTMLElement;
    let scrollingWrapper: HTMLElement;
    let scrollingWrapperRect: ClientRect;
    let startingPoint: TLassoRectangleVertex;
    let sessionPreviouslySelectedItems: string[] = [];
    let previouslySelectedItems: string[] = [];
    let shouldAppendItems: boolean = false;
    let autoScrollTimeout: number = null;
    let scrollingWrapperLowerBound: number = 0;

    const getSelectedArea = (event: MouseEvent): IRectangle => {
      if (!startingPoint) {
        throw new Error('No starting point for some reason.');
      }

      const { x, y } = startingPoint;

      const offsetX = Math.min(
        event.clientX - x,
        scrollingWrapperRect.right - x + (wrapperContentHeight > scrollingWrapperRect.height ? -SCROLLBAR_WIDTH : 0),
      );
      const offsetY = event.clientY + scrollingWrapper.scrollTop - y;

      return {
        x: offsetX > 0 ? x : x + offsetX,
        y: offsetY > 0 ? y : y + offsetY,
        offsetX: Math.abs(offsetX),
        offsetY: Math.abs(offsetY),
      };
    };

    const selectItemsWithinArea = (selectedArea: IRectangle): void => {
      const itemsWithinArea = pipe<ILassoRectangleItems, ILassoRectangleItems, string[]>(
        filter<TLassoRectangle>((rect) => ifRectanglesIntersect(selectedArea, rect)),
        keys,
      )(itemsRectanglesKeeper.rectangles);

      const selectedItems = shouldAppendItems
        ? uniq([...previouslySelectedItems, ...itemsWithinArea])
        : itemsWithinArea;

      if (selectedItems.length !== sessionPreviouslySelectedItems.length) {
        setTimeout(() => {
          onSelectItems(selectedItems);
        }, 0);

        sessionPreviouslySelectedItems = [...selectedItems];
      }
    };

    const updateScrollingWrapperRect = (scrollingWrapper) => {
      scrollingWrapperRect = scrollingWrapper.getBoundingClientRect();
      scrollingWrapperLowerBound = getRectLowerBound(scrollingWrapperRect);
    };

    const shouldBlockLasso = (lassoItems: ILassoItems) => !Object.keys(lassoItems).length;

    return {
      saveRef: () => (node) => (element = node),
      onLassoStart:
        ({ onSelectStart = noop, lassoItems }) =>
        (event: ReactMouseEvent<HTMLElement>) => {
          if (!scrollingWrapper || shouldBlockLasso(lassoItems)) {
            return;
          }

          const scrollingWrapperScrollTop = scrollingWrapper.scrollTop;

          startingPoint = {
            x: event.clientX,
            y: event.clientY + scrollingWrapperScrollTop,
          };

          if (!shouldAppendItems) {
            previouslySelectedItems = [];
          }

          onSelectStart();
        },
      onLassoMove:
        ({ setSelectedArea, hasFirstMoveDone, onFirstMove = noop, setFirstMoveDone, lassoItems }) =>
        (event: MouseEvent) => {
          if (shouldBlockLasso(lassoItems)) {
            return;
          }

          if (!startingPoint) {
            throw new Error('no starting point for some reason');
          }

          const selectedArea = getSelectedArea(event);

          if (selectedArea.offsetX === 0 && selectedArea.offsetY === 0) {
            return;
          }

          function startAutoScroll(
            scrollDirection: AutoScrollDirection = AutoScrollDirection.Bottom,
            selectedArea: IRectangle,
          ) {
            autoScrollTimeout = window.setTimeout(() => {
              const scrollingWrapperScrollTop: number = scrollingWrapper.scrollTop;
              const offsetDelta: number =
                (scrollDirection === AutoScrollDirection.Top && startingPoint.y <= selectedArea.y) ||
                (scrollDirection === AutoScrollDirection.Bottom && startingPoint.y > selectedArea.y)
                  ? -AUTO_SCROLL_DELTA
                  : AUTO_SCROLL_DELTA;
              const scrollDelta = scrollDirection === AutoScrollDirection.Top ? -AUTO_SCROLL_DELTA : AUTO_SCROLL_DELTA;
              const newOffsetY = Math.min(
                selectedArea.offsetY + offsetDelta,
                Math.max(wrapperContentHeight, scrollingWrapperRect.height) -
                  (selectedArea.y - scrollingWrapperRect.top),
              );

              const newSelectedArea = {
                ...selectedArea,
                y:
                  startingPoint.y > selectedArea.y
                    ? startingPoint.y - newOffsetY
                    : newOffsetY < 0
                    ? startingPoint.y + newOffsetY
                    : startingPoint.y,
                offsetY: Math.abs(newOffsetY),
              };

              scrollingWrapper.scroll({
                top: scrollingWrapperScrollTop + scrollDelta,
                behavior: 'auto',
              });

              setSelectedArea(newSelectedArea);

              selectItemsWithinArea(newSelectedArea);

              const disallowToAutoScroll = scrollDirection === AutoScrollDirection.Top && !scrollingWrapperScrollTop;

              if (!disallowToAutoScroll) {
                startAutoScroll(scrollDirection, newSelectedArea);
              }
            }, AUTO_SCROLL_TIMEOUT_DURATION);
          }

          if (!hasFirstMoveDone) {
            onFirstMove();
            setFirstMoveDone(true);
          }

          const { clientY } = event;
          const disallowAutoScrollTopWhenReachBound = clientY < scrollingWrapperRect.top && !scrollingWrapper.scrollTop;
          const disallowToAutoScrollBottomWhenReachBound =
            clientY > scrollingWrapperLowerBound &&
            scrollingWrapper.scrollTop + scrollingWrapperRect.height === wrapperContentHeight;

          if (
            (clientY < scrollingWrapperLowerBound && clientY > scrollingWrapperRect.top) ||
            disallowAutoScrollTopWhenReachBound ||
            disallowToAutoScrollBottomWhenReachBound
          ) {
            clearTimeout(autoScrollTimeout);
            autoScrollTimeout = null;
          }

          if (autoScrollTimeout) {
            return;
          }

          if (
            (clientY > scrollingWrapperLowerBound || clientY < scrollingWrapperRect.top) &&
            !disallowAutoScrollTopWhenReachBound &&
            !disallowToAutoScrollBottomWhenReachBound &&
            !autoScrollTimeout
          ) {
            const autoScrollDirection =
              clientY < scrollingWrapperRect.top ? AutoScrollDirection.Top : AutoScrollDirection.Bottom;
            startAutoScroll(autoScrollDirection, selectedArea);
          }

          setSelectedArea(selectedArea);
          selectItemsWithinArea(selectedArea);
        },
      onLassoEnd:
        ({ setSelectedArea, onSelectFinish, setFirstMoveDone, lassoItems }) =>
        (event: MouseEvent) => {
          if (shouldBlockLasso(lassoItems)) {
            return;
          }

          if (autoScrollTimeout) {
            clearTimeout(autoScrollTimeout);
            autoScrollTimeout = null;
          }

          setSelectedArea(null);

          const currentItemsRectangles = itemsRectanglesKeeper.rectangles;

          itemsRectanglesKeeper.rectangles = {};
          previouslySelectedItems = sessionPreviouslySelectedItems;

          if (!onSelectFinish) {
            return;
          }

          const selectedArea = getSelectedArea(event);

          if (selectedArea.offsetX === 0 && selectedArea.offsetY === 0) {
            // just a point
            return;
          }

          const selectedIds = pipe<ILassoRectangleItems, TRectanglePair[], TRectanglePair[], string[]>(
            toPairs,
            filter<TRectanglePair>(([, itemRectangle]) => ifRectanglesIntersect(selectedArea, itemRectangle)),
            map(head),
          )(currentItemsRectangles);

          setTimeout(() => {
            onSelectFinish(selectedIds);
          }, 0);
          setFirstMoveDone(false);
        },
      getElementRect: () => () => element && element.getBoundingClientRect(),
      willAppendItems: () => (event: KeyboardEvent) => {
        if ((event.metaKey || event.ctrlKey || event.shiftKey) && !shouldAppendItems) {
          shouldAppendItems = true;
        }
      },
      stopAppendItems: () => (event: KeyboardEvent) => {
        if (!event.metaKey && !event.ctrlKey && !event.shiftKey) {
          shouldAppendItems = false;
        }
      },
      updateLassoSelectedItems:
        ({ selectedItems, selectedArea }) =>
        () => {
          if (selectedItems && previouslySelectedItems.length !== selectedItems.length && !selectedArea) {
            previouslySelectedItems = pluck<string, MediaManagerSDKAnyItem, any>('id', selectedItems);
          }
        },
      updateItemsRectangles:
        ({ lassoItems, itemMarginRight }) =>
        () => {
          if (!scrollingWrapper) {
            return;
          }

          itemsRectanglesKeeper.rectangles = mapObjIndexed((element: HTMLElement) => {
            const { top, left, width, height } = element.getBoundingClientRect();

            return {
              x: left,
              y: top + scrollingWrapper.scrollTop,
              offsetX: width - itemMarginRight,
              offsetY: height,
            };
          }, lassoItems);
        },
      updateContentSize: () => () => {
        if (!scrollingWrapper) {
          return;
        }

        wrapperContentHeight = pipe<HTMLCollection, Element[], number[], number>(
          reject((element: Element) => element.getAttribute('data-element-type') === 'lasso'),
          map((element) => element.clientHeight),
          sum,
        )(scrollingWrapper.children);
      },
      saveScrollingElementWrapperRef: () => (element) => {
        scrollingWrapper = element;

        updateScrollingWrapperRect(scrollingWrapper);
      },
      saveScrollingComponentWrapperRef: () => (Component) => {
        if (scrollingWrapper) {
          return;
        }

        scrollingWrapper = Component.el;

        updateScrollingWrapperRect(scrollingWrapper);
      },
      handleWindowResize: () => () => {
        if (!scrollingWrapper) {
          return;
        }

        updateScrollingWrapperRect(scrollingWrapper);
      },
    };
  }),
  withHandlers(({ onLassoMove, onLassoEnd }) => {
    const handleLassoEnd = (event) => {
      document.removeEventListener('mousemove', onLassoMove);
      document.removeEventListener('mouseup', handleLassoEnd);

      onLassoEnd(event);
    };

    return {
      handleLassoStart:
        ({ onLassoStart }) =>
        (event) => {
          onLassoStart(event);
          document.addEventListener('mousemove', onLassoMove);
          document.addEventListener('mouseup', handleLassoEnd);
        },
      unsubscribeLassoEvents: () => () => {
        document.removeEventListener('mousemove', onLassoMove);
        document.removeEventListener('mouseup', handleLassoEnd);
      },
    };
  }),
  withProps(({ selectedArea, getElementRect }) => {
    const rect = selectedArea !== null ? getElementRect() : null;

    return {
      selectedAreaStyle:
        selectedArea !== null && rect !== null
          ? {
              top: selectedArea.y - rect.top,
              left: selectedArea.x - rect.left,
              width: selectedArea.offsetX,
              height: selectedArea.offsetY,
            }
          : null,
    };
  }),
  withContext(
    contextTypes,
    pick(['onLassoItemReady', 'onLassoItemDestroy', 'selectedAreaStyle', 'saveScrollingElementWrapperRef']),
  ),
  withLifecycle({
    onDidMount({ willAppendItems, stopAppendItems, handleWindowResize }) {
      window.addEventListener('keydown', willAppendItems);
      window.addEventListener('keyup', stopAppendItems);
      window.addEventListener('resize', handleWindowResize);
    },
    onWillUnmount({ unsubscribeLassoEvents, willAppendItems, stopAppendItems, handleWindowResize }) {
      unsubscribeLassoEvents();
      window.removeEventListener('keydown', willAppendItems);
      window.removeEventListener('keyup', stopAppendItems);
      window.removeEventListener('resize', handleWindowResize);
    },
    onDidUpdate(_, { updateLassoSelectedItems, updateItemsRectangles, updateContentSize }) {
      updateContentSize();
      updateItemsRectangles();
      updateLassoSelectedItems();
    },
  }),

  mapProps(omit(['onLassoStart', 'onLassoEnd', 'onLassoMove'])),
);
